import { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Space, Typography, Modal, Form, Input, InputNumber, Switch, Upload, Progress, message } from 'antd';
import { UploadOutlined, InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import { ModuleTopHeading } from '../../components';
import { ApiCalls, TableLoader } from '../../shared';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;
const { Dragger } = Upload;

const OtaUpdate = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(null);
  const [form] = Form.useForm();
  const [apkFile, setApkFile] = useState(null);

  const fetchVersions = async () => {
    setLoading(true);
    const res = await ApiCalls.OtaUpdateApi.listVersions();
    setLoading(false);
    if (res?.success && Array.isArray(res.data)) setData(res.data);
  };

  useEffect(() => {
    fetchVersions();
  }, []);

  const handleUpload = async () => {
    try {
      const values = await form.validateFields();
      if (!apkFile) {
        message.error(t('Required field'));
        return;
      }
      setUploading(true);
      setUploadPercent(0);
      const payload = {
        apk_file: apkFile,
        version_code: values.version_code,
        version_name: values.version_name,
        release_notes: values.release_notes || '',
        force_update: values.force_update ?? false,
      };
      const result = await ApiCalls.OtaUpdateApi.uploadApk(payload, (percent) => setUploadPercent(percent));
      setUploading(false);
      setUploadPercent(null);

      if (result?.success) {
        form.resetFields();
        setApkFile(null);
        setModalOpen(false);
        fetchVersions();
        return;
      }

      if (result?.errors && typeof result.errors === 'object') {
        const fields = Object.entries(result.errors).map(([name, errs]) => ({
          name,
          errors: Array.isArray(errs) ? errs : [String(errs)],
        }));
        form.setFields(fields);
        const flat = fields.flatMap((f) => f.errors);
        message.error(flat.length ? flat.join(' ') : result.message);
      } else if (result?.message) {
        message.error(result.message);
      }
    } catch (e) {
      setUploading(false);
      setUploadPercent(null);
    }
  };

  const columns = [
    {
      title: t('Version Code'),
      dataIndex: 'version_code',
      key: 'version_code',
      width: 100,
      sorter: (a, b) => (a.version_code || 0) - (b.version_code || 0),
    },
    {
      title: t('Version Name'),
      dataIndex: 'version_name',
      key: 'version_name',
      width: 120,
    },
    {
      title: t('Release Notes'),
      dataIndex: 'release_notes',
      key: 'release_notes',
      ellipsis: true,
      render: (v) => (v ? <Text ellipsis>{v}</Text> : '–'),
    },
    {
      title: t('Force Update'),
      dataIndex: 'force_update',
      key: 'force_update',
      width: 100,
      render: (v) => (v ? t('Yes') : t('No')),
    },
    {
      title: t('File Size'),
      dataIndex: 'file_size_mb',
      key: 'file_size_mb',
      width: 100,
      render: (v) => (v != null ? `${v} MB` : '–'),
    },
    {
      title: t('Active'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (v) => (v ? t('Yes') : t('No')),
    },
    {
      title: t('File Exists'),
      dataIndex: 'file_exists',
      key: 'file_exists',
      width: 100,
      render: (v) => (v ? t('Yes') : t('No')),
    },
    {
      title: t('Created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
    },
    {
      title: t('Action'),
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, row) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            disabled={!row.file_exists || !row.filename}
            onClick={() => ApiCalls.OtaUpdateApi.downloadApk(row.filename)}
          >
            {t('Download')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card className="radius-12 border0">
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <ModuleTopHeading name={t('OTA Update')} onClick={() => setModalOpen(true)} />
          </Col>
          <Col span={24}>
            <Table
              size="large"
              columns={columns}
              dataSource={data}
              rowKey="id"
              className="table-cover"
              scroll={{ x: 1000 }}
              pagination={{
                hideOnSinglePage: true,
                total: data.length,
                pageSize: 10,
                showTotal: (total) => (
                  <Button>{t('Total')}: {total}</Button>
                ),
              }}
              loading={{ ...TableLoader, spinning: loading }}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title={t('Upload APK')}
        open={modalOpen}
        onCancel={() => {
          if (!uploading) {
            setModalOpen(false);
            form.resetFields();
            setApkFile(null);
            setUploadPercent(null);
          }
        }}
        footer={null}
        width={520}
        destroyOnClose
        closable={!uploading}
        maskClosable={!uploading}
      >
        <Form form={form} layout="vertical" onFinish={handleUpload} initialValues={{ force_update: false }}>
          <Form.Item
            name="apk_file"
            label={t('APK File')}
            valuePropName="fileList"
            rules={[{ required: true, message: t('Required field') }]}
          >
            <Dragger
              accept=".apk"
              maxCount={1}
              beforeUpload={() => false}
              onChange={({ fileList }) => {
                const f = fileList?.[0]?.originFileObj;
                if (f) {
                  const fileName = f.name || '';
                  if (!fileName.toLowerCase().endsWith('.apk')) {
                    message.warning(t('File must have .apk extension'));
                    setApkFile(null);
                    form.setFieldValue('apk_file', []);
                    return;
                  }
                }
                setApkFile(f || null);
                form.setFieldValue('apk_file', fileList);
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: '#FE5112' }} />
              </p>
              <p className="ant-upload-text">{t('Select File')} (.apk)</p>
            </Dragger>
          </Form.Item>
          <Form.Item
            name="version_code"
            label={t('Version Code')}
            rules={[{ required: true, message: t('Required field') }, { type: 'number', min: 1, message: t('Please enter a valid input') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="e.g. 10" />
          </Form.Item>
          <Form.Item
            name="version_name"
            label={t('Version Name')}
            rules={[{ required: true, message: t('Required field') }, { max: 50, message: t('Please enter a valid input') }]}
          >
            <Input placeholder="e.g. 1.0.0" maxLength={50} />
          </Form.Item>
          <Form.Item name="release_notes" label={t('Release Notes')}>
            <Input.TextArea rows={3} placeholder={t('Release Notes')} maxLength={2000} showCount />
          </Form.Item>
          <Form.Item name="force_update" label={t('Force Update')} valuePropName="checked">
            <Switch />
          </Form.Item>
          {uploading && uploadPercent != null && (
            <Form.Item label={t('Upload progress')}>
              <Progress percent={uploadPercent} status="active" />
            </Form.Item>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={uploading} icon={<UploadOutlined />} disabled={uploading}>
                {uploading && uploadPercent != null
                  ? `${t('Upload')} ${uploadPercent}%`
                  : t('Upload')}
              </Button>
              <Button
                onClick={() => {
                  if (!uploading) {
                    setModalOpen(false);
                    form.resetFields();
                    setApkFile(null);
                    setUploadPercent(null);
                  }
                }}
                disabled={uploading}
              >
                {t('Cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export { OtaUpdate };
